import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { Divider } from "../../../../../components/Divider";
import Spacer from "../../../../../components/Spacer";
import { CheckboxGroup } from "../../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";
import useUnsavedChanges from "../../../../../hooks/useUnsavedChanges";
import { useAiSettingsDefaults } from "../../hooks";

import { SectionLayout, SettingsFormFooter, SettingsToggle, UnsavedChangesTracker } from "../../../Components";

// Extract keys dynamically from the schemas to stay in sync with schema changes
const getSchemaKeys = <T extends z.ZodObject<z.ZodRawShape>>(schema: T): Array<keyof z.infer<T>> => {
	return Object.keys(schema.shape) as Array<keyof z.infer<T>>;
};

const AIInsightsMeetingsSettingsTab = () => {
	const { value: isTeamMeetingsEnabled } = useFeatureFlagQuery("ff-is-team-meetings-enabled", false);

	// Team-meeting checkbox keys are prefixed at the form layer to avoid collisions with the
	// 1:1 section's identical-named fields (`keyAccomplishmentsAndWins`, `actionItemsAnalysis`).
	// `handleSave` strips the prefix when persisting back to `teamMeetingWorkspaceInsightsSettings`.
	const formSchema = AiSettingsSchema.shape.meetingWorkspaceInsightsSettings.extend({
		teamRecurringThemesAndTopics: z.boolean(),
		teamKeyAccomplishmentsAndWins: z.boolean(),
		teamRecurringChallengesAndBlockers: z.boolean(),
		teamNotableDiscussionsAndDecisions: z.boolean(),
		teamActionItemsAnalysis: z.boolean(),
	});
	const formRef = useRef<FormHandle<typeof formSchema>>(null);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// Unsaved changes tracking
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// toggles
	const isMeetingWorkspaceInsightsAssistantEnabled = data?.meetingWorkspaceInsightsEnabled ?? true;
	const isTeamMeetingWorkspaceInsightsEnabled = data?.teamMeetingWorkspaceInsightsEnabled ?? true;

	// toggle handlers
	const handleMeetingWorkspaceInsightsAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ meetingWorkspaceInsightsEnabled: val });
		toast.success(`1:1 Meeting Workspace Insights ${val === true ? "Enabled" : "Disabled"}`);
	};

	const handleTeamMeetingWorkspaceInsightsToggle = async (val: boolean) => {
		await updateAiSettings({ teamMeetingWorkspaceInsightsEnabled: val });
		toast.success(`Team Meeting Workspace Insights ${val === true ? "Enabled" : "Disabled"}`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({ value, resetForm }: { value: z.infer<typeof formSchema>; resetForm: () => void }) => {
		try {
			// Reconstruct nested structure from flat form value
			const meetingWorkspaceInsightsSettingsKeys = getSchemaKeys(
				AiSettingsSchema.shape.meetingWorkspaceInsightsSettings
			);

			const meetingWorkspaceInsightsSettings = Object.fromEntries(
				meetingWorkspaceInsightsSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.meetingWorkspaceInsightsSettings>;

			const teamMeetingWorkspaceInsightsSettings: z.infer<
				typeof AiSettingsSchema.shape.teamMeetingWorkspaceInsightsSettings
			> = {
				recurringThemesAndTopics: value.teamRecurringThemesAndTopics,
				keyAccomplishmentsAndWins: value.teamKeyAccomplishmentsAndWins,
				recurringChallengesAndBlockers: value.teamRecurringChallengesAndBlockers,
				notableDiscussionsAndDecisions: value.teamNotableDiscussionsAndDecisions,
				actionItemsAnalysis: value.teamActionItemsAnalysis,
			};

			await updateAiSettings({
				meetingWorkspaceInsightsSettings,
				teamMeetingWorkspaceInsightsSettings,
			});
			toast.success("Your changes have been saved");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings");
		}
	};

	// Checkbox options for 1:1 Meeting Workspace Insights
	const checkBoxOptions = [
		{ value: "progressAndGrowth", label: "Progress & Growth", dataTestId: "progress-and-growth-checkbox" },
		{
			value: "keyAccomplishmentsAndWins",
			label: "Key Accomplishments & Wins",
			dataTestId: "key-accomplishments-and-wins-checkbox",
		},
		{
			value: "developmentAreasAndGrowthOpportunities",
			label: "Development Areas & Growth Opportunities",
			dataTestId: "development-areas-and-growth-opportunities-checkbox",
		},
		{
			value: "notableDiscussionsAndDecisions",
			label: "Notable Discussions & Decisions",
			dataTestId: "notable-discussions-and-decisions-checkbox",
		},
		{ value: "actionItemsAnalysis", label: "Action Item Analysis", dataTestId: "action-items-analysis-checkbox" },
	];

	// Checkbox options for Team Meeting Workspace Insights
	const teamMeetingCheckboxOptions = [
		{
			value: "teamRecurringThemesAndTopics",
			label: "Recurring Themes & Topics",
			dataTestId: "team-recurring-themes-and-topics-checkbox",
		},
		{
			value: "teamKeyAccomplishmentsAndWins",
			label: "Key Accomplishments & Wins",
			dataTestId: "team-key-accomplishments-and-wins-checkbox",
		},
		{
			value: "teamRecurringChallengesAndBlockers",
			label: "Recurring Challenges & Blockers",
			dataTestId: "team-recurring-challenges-and-blockers-checkbox",
		},
		{
			value: "teamNotableDiscussionsAndDecisions",
			label: "Notable Discussions & Decisions",
			dataTestId: "team-notable-discussions-and-decisions-checkbox",
		},
		{
			value: "teamActionItemsAnalysis",
			label: "Action Item Analysis",
			dataTestId: "team-action-items-analysis-checkbox",
		},
	];

	const defaultValues = useAiSettingsDefaults({
		schema: formSchema,
		data,
		extractDefaults: (allDefaults) => ({
			...allDefaults.meetingWorkspaceInsightsSettings,
			teamRecurringThemesAndTopics: allDefaults.teamMeetingWorkspaceInsightsSettings.recurringThemesAndTopics,
			teamKeyAccomplishmentsAndWins: allDefaults.teamMeetingWorkspaceInsightsSettings.keyAccomplishmentsAndWins,
			teamRecurringChallengesAndBlockers:
				allDefaults.teamMeetingWorkspaceInsightsSettings.recurringChallengesAndBlockers,
			teamNotableDiscussionsAndDecisions:
				allDefaults.teamMeetingWorkspaceInsightsSettings.notableDiscussionsAndDecisions,
			teamActionItemsAnalysis: allDefaults.teamMeetingWorkspaceInsightsSettings.actionItemsAnalysis,
		}),
		extractData: (data) => ({
			...data.meetingWorkspaceInsightsSettings,
			teamRecurringThemesAndTopics: data.teamMeetingWorkspaceInsightsSettings?.recurringThemesAndTopics ?? true,
			teamKeyAccomplishmentsAndWins: data.teamMeetingWorkspaceInsightsSettings?.keyAccomplishmentsAndWins ?? true,
			teamRecurringChallengesAndBlockers:
				data.teamMeetingWorkspaceInsightsSettings?.recurringChallengesAndBlockers ?? true,
			teamNotableDiscussionsAndDecisions:
				data.teamMeetingWorkspaceInsightsSettings?.notableDiscussionsAndDecisions ?? true,
			teamActionItemsAnalysis: data.teamMeetingWorkspaceInsightsSettings?.actionItemsAnalysis ?? true,
		}),
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="ai-insights-meetings-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={formSchema}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="ai-insights-meetings-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>
			<SectionLayout
				key="meetings-settings-section"
				title="Meeting Workspace Insights Assistant"
				description="The Meeting Workspace Insights Assistant allows participants and admins to generate insights about all the meetings within a meeting workspace."
			>
				<SettingsToggle
					key="meeting-workspace-insights-toggle"
					id="enable-meeting-workspace-insights-assistant"
					label="Enable 1:1 Meeting Workspace Insights"
					description="Disabling this setting will hide the ability for participants and admins to generate insights for 1:1 meeting workspaces."
					checked={isMeetingWorkspaceInsightsAssistantEnabled}
					onCheckedChange={handleMeetingWorkspaceInsightsAssistantToggle}
					dataTestId="meeting-workspace-insights-toggle"
				/>

				<CheckboxGroup
					key="meetings-insights-content-checkboxes"
					title="1:1 Insights Content"
					description="Control each section of content inside your 1:1 meeting workspace insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the 1:1 meeting workspace."
					options={checkBoxOptions}
				/>
			</SectionLayout>

			{isTeamMeetingsEnabled && (
				<>
					<Spacer key="spacer-team-1" height={5} />
					<Divider key="divider-team" />
					<Spacer key="spacer-team-2" height={4} />

					<SectionLayout
						key="team-meetings-settings-section"
						title="Team Meeting Workspace Insights"
						description="The Team Meeting Workspace Insights Assistant allows participants and admins to generate insights about all the meetings within a team meeting workspace."
					>
						<SettingsToggle
							key="team-meeting-workspace-insights-toggle"
							id="enable-team-meeting-workspace-insights"
							label="Enable Team Meeting Workspace Insights"
							description="Disabling this setting will hide the ability for participants and admins to generate insights for team meeting workspaces."
							checked={isTeamMeetingWorkspaceInsightsEnabled}
							onCheckedChange={handleTeamMeetingWorkspaceInsightsToggle}
							dataTestId="team-meeting-workspace-insights-toggle"
						/>

						<CheckboxGroup
							key="team-meetings-insights-content-checkboxes"
							title="Team Meeting Insights Content"
							description="Control each section of content inside your team meeting workspace insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the team meeting workspace."
							options={teamMeetingCheckboxOptions}
						/>
					</SectionLayout>
				</>
			)}

			<SettingsFormFooter
				key="meetings-settings-footer"
				formId="ai-insights-meetings-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default AIInsightsMeetingsSettingsTab;
